import { BACKEND_URL } from "@/lib/backendUrl";
import { useCallback, useEffect, useState } from "react";
import { Award, FileSignature, Gavel, Loader2, RefreshCw, Scale, Users } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader,
  DialogTitle } from "@/components/ui/dialog";
import { Chip, Kpi, Panel } from "@/components/common/Chrome";
import { api, apiError } from "@/lib/api";

const API_BASE = `${BACKEND_URL}/api`;
const pretty = (s) => String(s || "").slice(0, 16).replace("T", " ");
const words = (s) => String(s || "").replace(/_/g, " ");

const F = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-[11.5px] text-muted-foreground">{label}</Label>
    {children}
    {hint && <p className="text-[10.5px] leading-relaxed text-muted-foreground">{hint}</p>}
  </div>
);

export const Results = ({ electionRef, onChanged }) => {
  const [data, setData] = useState(null);
  const [recounts, setRecounts] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    const [r, rc] = await Promise.all([
      api.get(`/elections/${electionRef}/results`),
      api.get(`/elections/${electionRef}/recount-requests`),
    ]);
    setData(r.data);
    setRecounts(rc.data);
  }, [electionRef]);

  useEffect(() => { load().catch((e) => toast.error(apiError(e))); }, [load]);

  const run = async (key, fn, message) => {
    setBusy(key);
    try {
      await fn();
      toast.success(message);
      await load();
      setDialog(null);
      onChanged?.();
    } catch (e) { toast.error(apiError(e)); }
    setBusy("");
  };

  if (!data) return <Panel title="Results" testId="panel-results-loading">
    <p className="text-[12.5px] text-muted-foreground">Opening the count…</p></Panel>;

  const r = data.results;

  if (!r) {
    return (
      <Panel title="Results" testId="panel-results-none"
             kicker="Nothing has been counted on this election yet"
             actions={data.may_count && (
               <Button size="sm" className="h-8 rounded-full" data-testid="count-button"
                       onClick={() => setDialog({ kind: "count", minute: "" })}>
                 <Scale className="mr-1.5 h-3.5 w-3.5" /> Count the papers
               </Button>
             )}>
        <p className="text-[12.5px] text-muted-foreground" data-testid="results-empty">
          {data.election_status === "voting_closed"
            ? "Voting has closed. The Commission may now count the sealed papers."
            : "The papers are counted once voting closes."}
        </p>
        {dialog?.kind === "count" && (
          <CountDialog dialog={dialog} setDialog={setDialog} busy={busy}
                       onSave={() => run("count",
                         () => api.post(`/elections/${electionRef}/count`,
                           { minute: dialog.minute }),
                         "The papers are counted — now have another commissioner certify it")} />
        )}
      </Panel>
    );
  }

  const winners = r.sections.flatMap((s) => s.winners.map((w) => w.label));

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Kpi testId="results-kpi-status" label="Count" icon={Scale} value={words(r.status)}
             sub={r.results_ref} />
        <Kpi testId="results-kpi-turnout" label="Turnout" accent="teal"
             value={`${r.turnout.percent}%`}
             sub={`${r.turnout.cast} of ${r.turnout.roll} · least ${r.turnout.minimum_percent}%`} />
        <Kpi testId="results-kpi-tests" label="Quorum & turnout" accent="violet" icon={Users}
             value={r.tests_passed ? "Passed" : "Failed"}
             sub={r.turnout.quorum
               ? `quorum ${r.turnout.quorum} · ${r.turnout.quorum_met ? "met" : "not met"}`
               : "no quorum set in the rulebook"} />
        <Kpi testId="results-kpi-elected" label="Elected" accent="emerald" icon={Award}
             value={winners.length} sub={`${r.sections.length} section(s) counted`} />
      </div>

      <Panel title="The count" testId="panel-results"
             kicker={`Counted by ${r.counted_by}${r.certified_by
               ? ` · certified by ${r.certified_by}` : " · awaiting certification"}`}
             actions={(
               <div className="flex flex-wrap items-center gap-2">
                 <Chip testId="results-status"
                       className={r.status === "declared"
                         ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
                         : "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"}>
                   {words(r.status)}
                 </Chip>
                 {data.may_count && (
                   <Button size="sm" variant="outline" className="h-8 rounded-full"
                           data-testid="recount-button"
                           onClick={() => setDialog({ kind: "count", minute: "" })}>
                     <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Count again
                   </Button>
                 )}
                 {data.may_certify && (
                   <Button size="sm" className="h-8 rounded-full" data-testid="certify-button"
                           onClick={() => setDialog({ kind: "certify", minute: "",
                             override_reason: "" })}>
                     Certify the count
                   </Button>
                 )}
                 {data.may_declare && (
                   <Button size="sm" className="h-8 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                           data-testid="declare-button"
                           onClick={() => setDialog({ kind: "declare", minute: "" })}>
                     Declare the Results
                   </Button>
                 )}
                 {data.certificate_ready && (
                   <a href={`${API_BASE}/elections/${electionRef}/results-certificate.pdf`}
                      target="_blank" rel="noreferrer" data-testid="results-certificate">
                     <Button size="sm" variant="outline" className="h-8 rounded-full">
                       <FileSignature className="mr-1.5 h-3.5 w-3.5" /> Certificate
                     </Button>
                   </a>
                 )}
               </div>
             )}>
        {!r.tests_passed && (
          <p className="mb-3 text-[12px] text-amber-700 dark:text-amber-300"
             data-testid="tests-failed">
            The quorum or least-turnout test failed. Certifying this count needs a written reason,
            and it will be printed on the certificate.
          </p>
        )}
        <div className="space-y-3">
          {r.sections.map((s) => (
            <div key={s.section_ref} className="rounded-xl border border-border p-3.5"
                 data-testid={`result-section-${s.section_ref}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <p className="text-[13px] font-semibold text-foreground">{s.title}</p>
                  <p className="text-[10.5px] text-muted-foreground">
                    {s.method_label} · {s.seats} seat(s) · {s.valid} valid · {s.abstain} abstained
                    {" "}· {s.blank} blank · {s.spoilt} spoilt
                    {s.quota ? ` · quota ${s.quota}` : ""}
                  </p>
                </div>
                <Chip testId={`result-outcome-${s.section_ref}`}
                      className={s.outcome === "tie"
                        ? "border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-300"
                        : "border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"}>
                  {words(s.outcome)}
                </Chip>
              </div>
              <div className="mt-2.5 space-y-1.5">
                {s.tallies.map((t) => (
                  <div key={t.option_ref} className="flex items-center gap-2"
                       data-testid={`tally-${t.option_ref}`}>
                    <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
                      <div className={`h-full rounded-full ${t.elected ? "bg-emerald-500"
                        : t.tied ? "bg-amber-500" : "bg-primary/40"}`}
                           style={{ width: `${s.valid ? Math.min(100,
                             (t.votes / Math.max(1, s.valid)) * 100) : 0}%` }} />
                    </div>
                    <span className="w-56 shrink-0 text-[12px] text-foreground">
                      {t.label}
                      {t.elected && <span className="pl-1.5 text-emerald-600 dark:text-emerald-400">elected</span>}
                      {t.tied && <span className="pl-1.5 text-amber-600 dark:text-amber-400">tied</span>}
                    </span>
                    <span className="w-14 shrink-0 text-right font-mono text-[11.5px] text-muted-foreground">
                      {t.votes}
                    </span>
                  </div>
                ))}
              </div>
              {s.outcome === "tie" && data.you_are_on_commission && (
                <div className="mt-2.5 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="h-8 rounded-full"
                          data-testid={`runoff-${s.section_ref}`}
                          onClick={() => run("runoff",
                            () => api.post(`/elections/${electionRef}/runoff`,
                              { minute: `Run-off for the tie on ${s.title}` }),
                            "A run-off between the tied candidates has been created")}>
                    <Gavel className="mr-1.5 h-3.5 w-3.5" /> Hold a run-off
                  </Button>
                  <Button size="sm" variant="ghost" className="h-8 rounded-full"
                          data-testid={`lots-${s.section_ref}`}
                          onClick={() => setDialog({ kind: "lots", section_ref: s.section_ref,
                            tied: s.tied, won_by: s.tied[0]?.label || "", minute: "",
                            witnessed_by: "" })}>
                    Draw lots
                  </Button>
                </div>
              )}
              {(r.rounds[s.section_ref] || []).length > 0 && s.count_method !== "plurality" && (
                <details className="mt-2" data-testid={`rounds-${s.section_ref}`}>
                  <summary className="cursor-pointer text-[11px] text-muted-foreground">
                    {r.rounds[s.section_ref].length} counting round(s) ·{" "}
                    {s.count_method.replace(/_/g, " ")}
                  </summary>
                  <div className="mt-1.5 space-y-1">
                    {r.rounds[s.section_ref].map((round) => (
                      <p key={round.round} className="text-[11px] text-muted-foreground">
                        Round {round.round}: {round.note}
                        {round.elected.length ? ` — elected ${round.elected.join(", ")}` : ""}
                        {round.eliminated.length ? ` — out ${round.eliminated.join(", ")}` : ""}
                      </p>
                    ))}
                  </div>
                </details>
              )}
            </div>
          ))}
        </div>
        {r.certification_minute && (
          <p className="mt-3 rounded-lg bg-muted px-3 py-2 text-[11.5px] text-foreground"
             data-testid="certification-minute">
            Certified by {r.certified_by} on {pretty(r.certified_at)} — {r.certification_minute}
            {r.override_reason ? ` (test failed: ${r.override_reason})` : ""}
          </p>
        )}
        {r.status === "declared" && (
          <p className="mt-2 text-[11.5px] text-muted-foreground" data-testid="declared-note">
            Declared on {pretty(r.declared_at)} by {r.declared_by}. The public results page is
            live at <span className="font-mono">/results/{electionRef}</span>. Candidates may ask
            for a recount until {pretty(r.recount_window?.closes_at)}.
          </p>
        )}
        {(r.lots || []).map((lot) => (
          <p key={lot.lot_ref} className="mt-2 text-[11.5px] text-muted-foreground"
             data-testid={`lot-${lot.lot_ref}`}>
            {lot.section_title}: tied between {lot.between.join(" and ")}, decided by lot in
            favour of {lot.won_by} — {lot.minute}
          </p>
        ))}
        {(r.recounts || []).map((rc) => (
          <p key={rc.recount_ref} className="mt-2 text-[11.5px] text-muted-foreground"
             data-testid={`recount-${rc.recount_ref}`}>
            Recount {rc.recount_ref} on request {rc.request_ref}:{" "}
            {rc.unchanged ? "no change to the result"
              : `the outcome changed in ${rc.changed_sections.length} section(s)`}. {rc.minute}
          </p>
        ))}
      </Panel>

      {/* recount requests */}
      <Panel title="Recount requests" testId="panel-recounts"
             kicker={r.recount_window?.closes_at
               ? `A candidate may ask until ${pretty(r.recount_window.closes_at)}`
               : "Open once the results are declared"}
             actions={data.you_are_a_candidate && data.recount_open && (
               <Button size="sm" variant="outline" className="h-8 rounded-full"
                       data-testid="ask-recount"
                       onClick={() => setDialog({ kind: "recount", reason: "" })}>
                 Ask for a recount
               </Button>
             )}>
        {(recounts?.items || []).length === 0 ? (
          <p className="text-[12.5px] text-muted-foreground" data-testid="recounts-empty">
            No recount has been asked for.
          </p>
        ) : (
          <div className="space-y-2">
            {recounts.items.map((item) => (
              <div key={item.request_ref} className="rounded-xl border border-border p-3"
                   data-testid={`recount-request-${item.request_ref}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <p className="text-[12.5px] font-medium text-foreground">
                    {item.asked_by_name} · {item.request_ref}
                  </p>
                  <Chip testId={`recount-status-${item.request_ref}`}
                        className="border-border bg-muted text-muted-foreground">
                    {item.actioned ? "recounted" : words(item.status)}
                  </Chip>
                </div>
                {item.reason && (
                  <p className="mt-1 text-[11.5px] text-muted-foreground">{item.reason}</p>
                )}
                {item.decision?.reason && (
                  <p className="mt-1 text-[11.5px] text-foreground">
                    {words(item.decision.decision)} by {item.decision.by} — {item.decision.reason}
                  </p>
                )}
                {data.you_are_on_commission && item.status === "requested" && (
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Button size="sm" className="h-8 rounded-full"
                            data-testid={`accept-recount-${item.request_ref}`}
                            onClick={() => setDialog({ kind: "decide", ref: item.request_ref,
                              decision: "accepted", reason: "" })}>
                      Accept
                    </Button>
                    <Button size="sm" variant="outline" className="h-8 rounded-full"
                            data-testid={`refuse-recount-${item.request_ref}`}
                            onClick={() => setDialog({ kind: "decide", ref: item.request_ref,
                              decision: "refused", reason: "" })}>
                      Refuse
                    </Button>
                  </div>
                )}
                {data.you_are_on_commission && item.status === "accepted" && !item.actioned && (
                  <Button size="sm" className="mt-2 h-8 rounded-full"
                          data-testid={`run-recount-${item.request_ref}`}
                          onClick={() => run("recount",
                            () => api.post(`/elections/${electionRef}/recount`,
                              { minute: "Recounted on the same sealed papers in the presence of "
                                        + "the Commission." }),
                            "The recount is done and recorded")}>
                    <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Run the recount
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* signatures */}
      <Panel title="Commissioners' signatures" testId="panel-signatures"
             kicker="The certificate is issued over these once the recount window closes">
        <div className="space-y-1.5">
          {(data.signatures || []).map((s) => (
            <p key={s.email} className="text-[12px] text-muted-foreground"
               data-testid={`signature-${s.email}`}>
              {s.name} — {s.signed ? `signed ${pretty(s.at)}` : "signature not held"}
            </p>
          ))}
        </div>
        {data.you_are_on_commission && (
          <div className="mt-3">
            <input type="file" accept="image/*" data-testid="signature-input"
                   onChange={async (e) => {
                     const file = e.target.files?.[0];
                     if (!file) return;
                     const body = new FormData();
                     body.append("file", file);
                     await run("signature",
                       () => api.post(`/elections/${electionRef}/signature`, body,
                         { headers: { "Content-Type": "multipart/form-data" } }),
                       "Your signature is on file for this election");
                   }}
                   className="text-[11.5px] text-muted-foreground" />
            <p className="mt-1 text-[10.5px] text-muted-foreground">
              Upload your own signature only — a PNG or JPG under 2 MB.
            </p>
          </div>
        )}
      </Panel>

      {/* dialogs */}
      {dialog?.kind === "count" && (
        <CountDialog dialog={dialog} setDialog={setDialog} busy={busy}
                     onSave={() => run("count",
                       () => api.post(`/elections/${electionRef}/count`,
                         { minute: dialog.minute }),
                       "The papers are counted")} />
      )}

      <Dialog open={dialog?.kind === "certify"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="certify-dialog">
          <DialogHeader><DialogTitle>Certify the count</DialogTitle>
            <DialogDescription>
              You are not the commissioner who ran the count. Your minute goes onto the
              certificate.
            </DialogDescription></DialogHeader>
          <F label="Certification minute" hint="At least 10 characters.">
            <Textarea rows={3} data-testid="certify-minute" value={dialog?.minute || ""}
                      onChange={(e) => setDialog({ ...dialog, minute: e.target.value })} />
          </F>
          {!r.tests_passed && (
            <F label="Reason for certifying despite the failed test">
              <Textarea rows={2} data-testid="certify-override"
                        value={dialog?.override_reason || ""}
                        onChange={(e) => setDialog({ ...dialog,
                          override_reason: e.target.value })} />
            </F>
          )}
          <DialogFooter>
            <Button data-testid="certify-save" disabled={busy === "certify"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("certify",
                      () => api.post(`/elections/${electionRef}/certify`, dialog),
                      "The count is certified — you may now declare the results")}>
              {busy === "certify" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Certify
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.kind === "declare"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="declare-dialog">
          <DialogHeader><DialogTitle>Declare the Results</DialogTitle>
            <DialogDescription>
              The public results page goes live at once, every member is told, and candidates get
              24 hours to ask for a recount.
            </DialogDescription></DialogHeader>
          <F label="Declaration minute">
            <Textarea rows={3} data-testid="declare-minute" value={dialog?.minute || ""}
                      onChange={(e) => setDialog({ ...dialog, minute: e.target.value })} />
          </F>
          <DialogFooter>
            <Button data-testid="declare-save" disabled={busy === "declare"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("declare",
                      () => api.post(`/elections/${electionRef}/declare`,
                        { minute: dialog.minute }),
                      "The results are declared and published")}>
              {busy === "declare" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Declare them
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.kind === "recount"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="recount-dialog">
          <DialogHeader><DialogTitle>Ask for a recount</DialogTitle>
            <DialogDescription>
              Only a candidate on this ballot may ask, and only within 24 hours of the
              declaration. The Commission will accept or refuse it in writing.
            </DialogDescription></DialogHeader>
          <F label="Your reason" hint="At least 20 characters.">
            <Textarea rows={4} data-testid="recount-reason" value={dialog?.reason || ""}
                      onChange={(e) => setDialog({ ...dialog, reason: e.target.value })} />
          </F>
          <DialogFooter>
            <Button data-testid="recount-save" disabled={busy === "ask"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("ask",
                      () => api.post(`/elections/${electionRef}/recount-requests`,
                        { reason: dialog.reason }),
                      "Your request is with the Commission")}>
              {busy === "ask" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Send it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.kind === "decide"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="decide-dialog">
          <DialogHeader><DialogTitle>
              {dialog?.decision === "accepted" ? "Accept the recount request"
                : "Refuse the recount request"}
            </DialogTitle>
            <DialogDescription>
              The candidate is told your reason and it stays on the record.
            </DialogDescription></DialogHeader>
          <F label="Reason" hint="At least 10 characters.">
            <Textarea rows={3} data-testid="decide-reason" value={dialog?.reason || ""}
                      onChange={(e) => setDialog({ ...dialog, reason: e.target.value })} />
          </F>
          <DialogFooter>
            <Button data-testid="decide-save" disabled={busy === "decide"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("decide",
                      () => api.post(`/elections/recount-requests/${dialog.ref}/decide`,
                        { decision: dialog.decision, reason: dialog.reason }),
                      "The decision is recorded and the candidate has been told")}>
              {busy === "decide" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Record it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={dialog?.kind === "lots"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="lots-dialog">
          <DialogHeader><DialogTitle>Draw lots</DialogTitle>
            <DialogDescription>
              Only where a run-off has left the candidates still level. The draw is minuted.
            </DialogDescription></DialogHeader>
          <F label="Won by">
            <Select value={dialog?.won_by}
                    onValueChange={(v) => setDialog({ ...dialog, won_by: v })}>
              <SelectTrigger data-testid="lots-winner"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(dialog?.tied || []).map((t) => (
                  <SelectItem key={t.option_ref} value={t.label}>{t.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </F>
          <F label="Minute" hint="At least 10 characters.">
            <Textarea rows={3} data-testid="lots-minute" value={dialog?.minute || ""}
                      onChange={(e) => setDialog({ ...dialog, minute: e.target.value })} />
          </F>
          <F label="Witnessed by">
            <Input data-testid="lots-witness" value={dialog?.witnessed_by || ""}
                   onChange={(e) => setDialog({ ...dialog, witnessed_by: e.target.value })} />
          </F>
          <DialogFooter>
            <Button data-testid="lots-save" disabled={busy === "lots"}
                    className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                    onClick={() => run("lots",
                      () => api.post(`/elections/${electionRef}/draw-lots`, {
                        section_ref: dialog.section_ref, won_by: dialog.won_by,
                        minute: dialog.minute, witnessed_by: dialog.witnessed_by }),
                      "The draw is recorded")}>
              {busy === "lots" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Record it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const CountDialog = ({ dialog, setDialog, busy, onSave }) => (
  <Dialog open onOpenChange={(o) => !o && setDialog(null)}>
    <DialogContent className="max-w-lg" data-testid="count-dialog">
      <DialogHeader><DialogTitle>Count the papers</DialogTitle>
        <DialogDescription>
          Each section is counted by the system chosen before voting day. Quorum and turnout are
          tested against the frozen rulebook. Another commissioner must certify it afterwards.
        </DialogDescription></DialogHeader>
      <F label="Note for the record">
        <Textarea rows={3} data-testid="count-minute" value={dialog?.minute || ""}
                  onChange={(e) => setDialog({ ...dialog, minute: e.target.value })} />
      </F>
      <DialogFooter>
        <Button data-testid="count-save" disabled={busy === "count"}
                className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                onClick={onSave}>
          {busy === "count" && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Count them
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);
